import React, {useEffect, useRef, useState} from 'react'
import '../styles/marketValueSummaryV2.scss'
import PrintIcon from '../../../assets/icons/print-icon'
import CheckIcon from '../../../assets/icons/check-icon'
import ArrowDownIcon from '../../../assets/icons/arrow-down-icon'
import OpenNewIcon from '../../../assets/icons/open-new-icon'
import ValueCheckIcon from '../../../assets/icons/value-check-icon'
import MarketValueSummaryV2Loader from './marketValueSummaryV2Loader'
import MSRPChart from './msrpChart'

const decimalFormatter = new Intl.NumberFormat('en-US', { style: 'decimal' })

const formatMoney = (v) => Number.isFinite(v) ? `$${decimalFormatter.format(v)}` : null
const formatMiles = (v) => Number.isFinite(v) ? `${decimalFormatter.format(v)} mi` : null
const isValidMsrp = (msrp) => Number.isFinite(msrp) && msrp > 0
const formatMsrpPercent = (value, msrp) =>
    Number.isFinite(value) && isValidMsrp(msrp) ? Math.round((value / msrp) * 100) : null
const joinParts = (...parts) => parts.filter(p => p !== null && p !== undefined && p !== '').join(' ')

const NextStepIcon = ({ url, alt }) => {
    const [failed, setFailed] = useState(false)

    if (!url || failed) {
        return <OpenNewIcon />
    }

    return (
        <img
            src={url}
            alt={alt}
            onError={() => setFailed(true)}
        />
    )
}

function MarketValueSummaryV2({appraisalInfo}){
    const [windowStickerExpanded, setWindowStickerExpanded] = useState(false)
    const [reconditioningReceiptExpanded, setReconditioningReceiptExpanded] = useState(false)
    const [windowStickerMounted, setWindowStickerMounted] = useState(false)
    const [reconditioningReceiptMounted, setReconditioningReceiptMounted] = useState(false)
    const reconditioningReceiptRef = useRef(null)
    const scrollToReceiptPending = useRef(false)
    const [summaryExpanded, setSummaryExpanded] = useState(false)
    const [summaryOverflows, setSummaryOverflows] = useState(false)
    const summaryBodyRef = useRef(null)

    useEffect(() => {
        if (scrollToReceiptPending.current && reconditioningReceiptExpanded) {
            scrollToReceiptPending.current = false
            reconditioningReceiptRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }, [reconditioningReceiptExpanded])

    useEffect(() => {
        if (windowStickerExpanded) setWindowStickerMounted(true)
    }, [windowStickerExpanded])

    useEffect(() => {
        if (reconditioningReceiptExpanded) setReconditioningReceiptMounted(true)
    }, [reconditioningReceiptExpanded])

    useEffect(() => {
        const el = summaryBodyRef.current
        if (!el) return
        const measure = () => {
            if (!summaryExpanded) setSummaryOverflows(el.scrollHeight > el.clientHeight + 1)
        }
        measure()
        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [appraisalInfo, summaryExpanded])

    const handleViewRepairReceipt = (event) => {
        event.preventDefault()
        if (reconditioningReceiptExpanded) {
            reconditioningReceiptRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            return
        }
        scrollToReceiptPending.current = true
        setReconditioningReceiptExpanded(true)
    }

    const renderComparablesMSRPPercentage = (msrp, msrpPercentage, isCurrentVehicle = false) => {
        if (!isValidMsrp(msrp)) {
            return null
        }
        if (Number.isFinite(msrpPercentage)) {
            return `${Math.round(msrpPercentage)}% MSRP (${formatMoney(msrp)})`
        }
        if (isCurrentVehicle) {
            const percent = formatMsrpPercent(appraisalInfo.vehicle.dealer_retail_value, msrp)
            return percent === null ? null : `${percent}% MSRP (${formatMoney(msrp)})`
        }
        return null
    }

    const renderPriceDelta = (comparablePrice) => {
        if (!Number.isFinite(comparablePrice) || !Number.isFinite(appraisalInfo.vehicle.dealer_retail_value)) {
            return null
        }
        const delta = comparablePrice - appraisalInfo.vehicle.dealer_retail_value
        if (delta === 0) {
            return null
        }
        return (
            <span className="comparables-vehicle-price-delta">
                <span className="comparables-vehicle-price-delta-sign">{delta > 0 ? '+' : '\u2212'}</span>
                {formatMoney(Math.abs(delta))}
            </span>
        )
    }

    const renderComparablesTable = (comparables) => {
        return comparables.map((comp, index) => {
            const msrpLabel = renderComparablesMSRPPercentage(comp.msrp, comp.msrp_percentage)
            const mileage = formatMiles(comp.odometer)
            const price = formatMoney(comp.retail_price)
            const status = comp.status?.toLowerCase()
            return (
                <div className="market-value-summary-comparables-table-item" key={index}>
                    <div className="comparables-vehicle-info">
                        <span>
                            {joinParts(comp.year, comp.make, comp.model)}
                            {
                                status === 'sold' ? <span className="comparables-vehicle-status comparables-vehicle-status-sold">Sold</span> :
                                status === 'active' ? <span className="comparables-vehicle-status comparables-vehicle-status-active">Active</span> :
                                null
                            }
                        </span>
                        <span>
                            {mileage && <span>{mileage}</span>}
                            {msrpLabel && <span>{msrpLabel}</span>}
                        </span>
                    </div>
                    <div className="comparables-vehicle-price">
                        {
                            status === 'sold' ? <span className='comparables-vehicle-price-subtitle'>Last Listed</span> :
                            status === 'active' ? <span className='comparables-vehicle-price-subtitle'>Current Price</span> :
                            null
                        }
                        {price && <span>{price}</span>}
                        {renderPriceDelta(comp.retail_price)}
                    </div>
                </div>
            )
        })
    }

    const renderHeaderValuation = () => {
        const priceDifference = appraisalInfo?.vehicle?.price_difference
        if (!priceDifference || !Number.isFinite(priceDifference.amount)) {
            return null
        }
        const amount = <em>{formatMoney(priceDifference.amount)}</em>
        if (priceDifference.position === 'below_market') {
            return <span>Priced {amount} below market value*</span>
        }
        return <span>Priced within {amount} of market value*</span>
    }

    const handleNextStepClick = (url, opens_in_new_tab) => {
        if (!url) return
        if (opens_in_new_tab) {
            window.open(url, '_blank', 'noopener,noreferrer')
        } else {
            window.location.href = url
        }
    }

    const renderNextStepsButtons = (next_steps) => {
        if (!next_steps || next_steps.length === 0) {
            return null
        }
        return (
            <div className="market-value-summary-next-steps-action-buttons">
                {next_steps.map((step, index) => (
                    <button key={index} onClick={() => handleNextStepClick(step.url, step.opens_in_new_tab)}>
                        <div className="action-button-text">
                            <span>{step.title}</span>
                            <span>{step.tagline}</span>
                        </div>
                        <div className="action-button-icon">
                            <NextStepIcon url={step.icon_url} alt={step.title} />
                        </div>
                    </button>
                ))}
            </div>
        )
    }

    if (!appraisalInfo) {
        return <MarketValueSummaryV2Loader />
    }

    const vehicle = appraisalInfo.vehicle
    const hasMsrp = isValidMsrp(vehicle.msrp)
    const dealerPrice = formatMoney(vehicle.dealer_retail_value)
    const dealerMsrpPercent = formatMsrpPercent(vehicle.dealer_retail_value, vehicle.msrp)
    const marketValue = formatMoney(vehicle.ipacket_retail_value_average)
    const marketMsrpPercent = formatMsrpPercent(vehicle.ipacket_retail_value_average, vehicle.msrp)
    const priceDifferencePosition = vehicle.price_difference?.position
    const priceDiffAmount = vehicle.price_difference?.amount
    const vehicleMileage = formatMiles(vehicle.mileage)
    const currentVehicleMsrpLabel = renderComparablesMSRPPercentage(vehicle.msrp, null, true)

    return (
        <div className="market-value-summary-container">
            <div className="market-value-summary-section market-value-summary-header">
                <div className="market-value-summary-header-text">
                    <div className="market-value-summary-header-title">
                        <h1>Market Value Summary</h1>
                        <h4>Powered by iPacket VIN-level Vehicle Intelligence</h4>
                        <button className="market-value-summary-header-print" onClick={() => window.print()}>
                            <PrintIcon /> Print
                        </button>
                    </div>
                    <div className="market-value-summary-header-vehicle">
                        <span>{joinParts(vehicle.year, vehicle.make, vehicle.model, vehicle.trim)}</span>
                        {vehicle.vin && <span>{`VIN: ${vehicle.vin}`}</span>}
                    </div>
                    <div className="market-value-summary-header-valuation">
                        <span>
                            <button><ValueCheckIcon /> Best Value</button>
                            {renderHeaderValuation()}
                        </span>
                    </div>
                </div>
                <div className="market-value-summary-header-image">
                    <img src={appraisalInfo.vehicle.vehicle_photo} alt="Vehicle Image" />
                </div>
            </div>

            <div className="market-value-summary-section market-value-summary-price-breakdown">
                <div className="strong-buy-candidate">
                    <div className="strong-buy-candidate-title"><ValueCheckIcon /> Vehicle Analysis</div>
                    <div className="strong-buy-candidate-description">
                        <p>{appraisalInfo.vehicle.summary_headline}</p>
                        <p
                            ref={summaryBodyRef}
                            className={`strong-buy-candidate-summary-body${summaryExpanded ? ' expanded' : ''}`}
                        >
                            {appraisalInfo.vehicle.summary_body}
                        </p>
                        {(summaryOverflows || summaryExpanded) && (
                            <p>
                                <a
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={summaryExpanded}
                                    onClick={() => setSummaryExpanded(v => !v)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            setSummaryExpanded(v => !v)
                                        }
                                    }}
                                >
                                    {summaryExpanded ? 'See less' : 'See more'}
                                </a>
                            </p>
                        )}
                    </div>
                </div>

                <div className="market-value-summary-price-breakdown-prices-container">
                    <div className="market-value-summary-price-breakdown-prices">
                        {dealerPrice && (
                            <div className="market-value-summary-price-breakdown-price-item">
                                <span>Dealer Price</span>
                                <span>{dealerPrice}</span>
                                {dealerMsrpPercent !== null && <span>{`${dealerMsrpPercent}% of Original MSRP`}</span>}
                            </div>
                        )}
                        {marketValue && (
                            <div className="market-value-summary-price-breakdown-price-item">
                                <span>Market Value*</span>
                                <span>{marketValue}</span>
                                {marketMsrpPercent !== null && <span>{`${marketMsrpPercent}% of Original MSRP`}</span>}
                            </div>
                        )}
                        {priceDifferencePosition && Number.isFinite(priceDiffAmount) && (
                            <div className="market-value-summary-price-breakdown-price-item market-value-summary-price-breakdown-price-item-savings">
                                <span>Priced</span>
                                <span>{formatMoney(Math.abs(priceDiffAmount))}</span>
                                <span>
                                    {priceDifferencePosition === 'below_market' ? 'below market value*' : 'within market value*'}
                                </span>
                            </div>
                        )}
                    </div>

                    {appraisalInfo.vehicle.has_reconditioning_data && (
                        <div className="market-value-summary-price-breakdown-cta">
                            <span><CheckIcon /> Reconditioning completed</span>
                            <span>This vehicle has repair records available for review.</span>
                            {appraisalInfo.vehicle.reconditioning_receipt_url && (
                                <span><button onClick={handleViewRepairReceipt}>View repair receipt</button></span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {appraisalInfo.next_steps && appraisalInfo.next_steps.length > 0 && (
                <div className="market-value-summary-section market-value-summary-next-steps">
                    <h1>Ready to take the next step?</h1>
                    {renderNextStepsButtons(appraisalInfo.next_steps)}
                </div>
            )}

            {hasMsrp && appraisalInfo.comparables && appraisalInfo.comparables.length > 0 && (
                <div className="market-value-summary-section market-value-summary-msrp-comparison">
                    <h1>Where this vehicle sits</h1>
                    <h4>{`Price as a percentage of original MSRP (${formatMoney(vehicle.msrp)})`}</h4>
                    <div className="market-value-summary-msrp-comparison-chart">
                        <MSRPChart
                            msrp={appraisalInfo.vehicle.msrp}
                            price={appraisalInfo.vehicle.dealer_retail_value}
                            dealerRetailValue={appraisalInfo.vehicle.dealer_retail_value}
                            comparables={appraisalInfo.comparables}
                        />
                    </div>
                </div>
            )}

            {appraisalInfo.comparables && appraisalInfo.comparables.length > 0 && (
                <div className="market-value-summary-section market-value-summary-comparables">
                    <h1>Comparable Vehicles Nearby</h1>
                    <h4>Price difference shown is versus this vehicle.</h4>
                    <div className="market-value-summary-comparables-table">
                        <div className="market-value-summary-comparables-table-item current-vehicle">
                            <div className="comparables-vehicle-info-title">This Vehicle</div>
                            <div className="comparables-vehicle-info">
                                <span>{joinParts(vehicle.year, vehicle.make, vehicle.model)}</span>
                                <span>
                                    {vehicleMileage && <span>{vehicleMileage}</span>}
                                    {currentVehicleMsrpLabel && <span>{currentVehicleMsrpLabel}</span>}
                                </span>
                            </div>
                            <div className="comparables-vehicle-price">
                                <span className='comparables-vehicle-price-subtitle'>Current Price</span>
                                {dealerPrice && <span>{dealerPrice}</span>}
                            </div>
                        </div>
                        {renderComparablesTable(appraisalInfo.comparables)}
                    </div>
                </div>
            )}

            {appraisalInfo.vehicle.window_sticker_url && (
                <div
                    className="market-value-summary-section market-value-summary-attached-modules"
                    onClick={() => setWindowStickerExpanded(v => !v)}
                >
                    <div className="attached-modules-header">
                        <div className="attached-modules-text">
                            <h1>Original Window Sticker</h1>
                            <h4>Expand to review the original factory equipment, added options, VIN details, and total suggested price.</h4>
                        </div>
                        <button
                            className="attached-modules-expand-icon"
                            aria-expanded={windowStickerExpanded}
                            data-expanded={windowStickerExpanded ? 'true' : 'false'}
                        >
                            <ArrowDownIcon />
                        </button>
                    </div>
                    {windowStickerMounted && (
                        <div
                            className="attached-modules-content"
                            data-expanded={windowStickerExpanded ? 'true' : 'false'}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <iframe src={appraisalInfo.vehicle.window_sticker_url} title="Original Window Sticker" />
                        </div>
                    )}
                </div>
            )}

            {appraisalInfo.vehicle.reconditioning_receipt_url && (
                <div
                    className="market-value-summary-section market-value-summary-attached-modules"
                    id="reconditioning-receipt"
                    ref={reconditioningReceiptRef}
                    onClick={() => setReconditioningReceiptExpanded(v => !v)}
                >
                    <div className="attached-modules-header">
                        <div className="attached-modules-text">
                            <h1>Verified Reconditioning Receipt</h1>
                            <h4>Expand to review a transparency-style reconditioning report with repair investment, evidence, and completed milestones.</h4>
                        </div>
                        <button
                            className="attached-modules-expand-icon"
                            aria-expanded={reconditioningReceiptExpanded}
                            data-expanded={reconditioningReceiptExpanded ? 'true' : 'false'}
                        >
                            <ArrowDownIcon />
                        </button>
                    </div>
                    {reconditioningReceiptMounted && (
                        <div
                            className="attached-modules-content"
                            data-expanded={reconditioningReceiptExpanded ? 'true' : 'false'}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <iframe src={appraisalInfo.vehicle.reconditioning_receipt_url} title="Verified Reconditioning Receipt" />
                        </div>
                    )}
                </div>
            )}

            <div className="market-value-summary-section market-value-summary-disclaimer">
                <p>
                    *Disclaimer: The pricing information contained in this packet is provided for general reference only.
                    All listed prices are estimates and approximations based on available market data at the time of publication.
                    These values should not be relied upon as precise or guaranteed figures for the purposes of vehicle appraisals,
                    financing, insurance, resale, or trade-in decisions. Actual market value may vary based on individual vehicle condition,
                    location, and other market dynamics.
                </p>
            </div>
        </div>
    )
}

export default MarketValueSummaryV2;
